import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ShieldStar, SquaresFour, Briefcase, ClockClockwise, Timer, GearSix, SignOut, List, Receipt, Gavel, CalendarBlank, CalendarCheck, ChartBar, ChartLine, Shield, MinusCircle, ChartLineUp, Storefront, PiggyBank, Target, Bank, Briefcase as BriefcaseMetal, ChartPieSlice } from "@phosphor-icons/react";
import { NotificationBell } from "@/components/NotificationBell";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard", end: true },
  { to: "/marketplace", label: "Shift Marketplace", icon: Storefront, testId: "nav-marketplace" },
  { to: "/analytics", label: "Analytics", icon: ChartLine, testId: "nav-analytics" },
  { to: "/projections", label: "Projections", icon: ChartLineUp, testId: "nav-projections" },
  { to: "/retirement", label: "Retirement Planner", icon: PiggyBank, testId: "nav-retirement" },
  { to: "/finance", label: "My Financial Centre", icon: ChartPieSlice, testId: "nav-finance" },
  { to: "/goals", label: "Goal Planner", icon: Target, testId: "nav-goals" },
  { to: "/tax", label: "Tax Centre", icon: Bank, testId: "nav-tax" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, testId: "nav-calendar" },
  { to: "/schedule", label: "Schedule & Leave", icon: CalendarCheck, testId: "nav-schedule" },
  { to: "/paid-duty", label: "Paid Duty", icon: Briefcase, testId: "nav-paid-duty" },
  { to: "/court", label: "Court Time", icon: Gavel, testId: "nav-court" },
  { to: "/overtime", label: "Overtime", icon: Timer, testId: "nav-overtime" },
  { to: "/regular-hours", label: "Regular Hours", icon: ClockClockwise, testId: "nav-regular-hours" },
  { to: "/deductions", label: "Deductions", icon: MinusCircle, testId: "nav-deductions" },
  { to: "/paycheques", label: "Paycheques", icon: Receipt, testId: "nav-paycheques" },
  { to: "/reports", label: "Reports", icon: ChartBar, testId: "nav-reports" },
  { to: "/settings", label: "Settings", icon: GearSix, testId: "nav-settings" },
];

const adminLink = { to: "/admin", label: "Admin", icon: Shield, testId: "nav-admin" };
const preparerLink = { to: "/preparer", label: "Preparer Cases", icon: BriefcaseMetal, testId: "nav-preparer" };

function SidebarContent({ user, onLogout, onNavigate }) {
  const navLinks = user?.role === "admin" ? [...links, adminLink] : user?.role === "tax_preparer" ? [...links, preparerLink] : links;
  return (
    <>
      <div className="px-6 py-6 border-b border-border flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
          <ShieldStar className="h-6 w-6 text-primary-foreground" weight="fill" />
        </div>
        <div>
          <h1 className="text-lg font-black tracking-tight leading-none">MyPayAlert</h1>
          <span className="overline text-muted-foreground">Payroll Suite</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {navLinks.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`
            }
          >
            <l.icon className="h-5 w-5" weight="bold" />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-border">
        <div className="mb-3 px-2">
          <p className="text-sm font-semibold truncate" data-testid="sidebar-username">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
        >
          <SignOut className="h-5 w-5" weight="bold" />
          Sign out
        </button>
      </div>
    </>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/60 sticky top-0 h-screen">
        <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => {}} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop top bar */}
        <header className="hidden md:flex items-center justify-end px-10 py-3 border-b border-border bg-card/40 sticky top-0 z-20">
          <NotificationBell />
        </header>
        {/* Mobile top bar with hamburger */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card sticky top-0 z-30">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Open navigation" className="text-foreground p-1 -ml-1">
                <List className="h-6 w-6" weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="p-0 w-72 max-w-[85vw] bg-card border-r border-border flex flex-col [&>button]:hidden"
              data-testid="mobile-nav-drawer"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setDrawerOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <ShieldStar className="h-6 w-6 text-primary" weight="fill" />
            <span className="font-black tracking-tight">MyPayAlert</span>
          </div>
          <NotificationBell />
        </header>

        <main className="flex-1 px-5 md:px-10 py-8 max-w-6xl w-full">{children}</main>
      </div>
    </div>
  );
}
